<template>
  <md-dialog :md-active.sync="showDialog">
    <md-dialog-title>Create a new Tenant</md-dialog-title>

    <md-dialog-content class="md-scrollbar">
      <ValidationObserver ref="tenantForm">
        <div class="md-layout">
          <div class="md-layout-item md-size-50">
            <div class="md-layout-item md-size-100">
              <h6><strong>Tenant Information</strong></h6>

              <ValidationProvider rules="required" v-slot="{ errors }">
                <md-field :class="{ 'md-invalid': errors.length > 0 }">
                  <label>Name</label>
                  <md-input v-model="tenant.name" maxlength="50"></md-input>
                  <span class="md-error">{{ errors[0] }}</span>
                </md-field>
              </ValidationProvider>

              <ValidationProvider
                :rules="{ required: true, regex: /^[a-z-_]+$/ }"
                v-slot="{ errors }"
              >
                <md-field :class="{ 'md-invalid': errors.length > 0 }">
                  <label>Tenant Code</label>
                  <md-input
                    v-model="tenant.tenantCode"
                    v-lowercase
                    type="text"
                    maxlength="13"
                    :disabled="isEditMode"
                  ></md-input>
                  <span class="md-helper-text">
                    eg.&nbsp;
                    <code>customer-name</code>
                    &nbsp;or&nbsp;
                    <code>customer-name-countrycode</code>
                    .
                  </span>
                  <span class="md-error">{{ errors[0] }}</span>
                </md-field>
              </ValidationProvider>

              <ValidationProvider
                :rules="{ required: true, regex: /^[a-z-_]+$/ }"
                v-slot="{ errors }"
              >
                <md-field :class="{ 'md-invalid': errors.length > 0 }">
                  <md-tooltip
                    md-direction="top"
                    style="max-width: 800px; text-align: left"
                  >
                    <div>
                      This value should be a logical name for a group of tenants
                      that belong to the customer,
                      <br />
                      where you can easily identify which tenant codes belong to
                      this group.
                      <hr />
                      E.g. tenant group
                      <code>echo</code>
                      would belong to tenant codes
                      <code>echo-claims</code>
                      and
                      <code>echo-uw</code>
                      .
                    </div>
                  </md-tooltip>
                  <label>Tenant Group</label>
                  <md-input
                    v-model="tenant.tenantGroup"
                    v-lowercase
                    type="text"
                    maxlength="250"
                    :disabled="isEditMode"
                  ></md-input>
                  <span class="md-error">{{ errors[0] }}</span>
                </md-field>
              </ValidationProvider>

              <ValidationProvider rules="required" v-slot="{ errors }">
                <md-field :class="{ 'md-invalid': errors.length > 0 }">
                  <label>Domain</label>
                  <md-select
                    name="domain"
                    id="domain"
                    v-model="selectedDomains"
                    multiple
                  >
                    <md-option
                      v-for="item in domains"
                      :key="item.id.toLowerCase()"
                      :value="item.id.toLowerCase()"
                    >
                      {{ item.description }}
                    </md-option>
                  </md-select>
                  <span class="md-error">{{ errors[0] }}</span>
                </md-field>
              </ValidationProvider>

              <ValidationProvider
                v-if="isFsc"
                rules="required"
                v-slot="{ errors }"
              >
                <md-field :class="{ 'md-invalid': errors.length > 0 }">
                  <label>FSC default configuration</label>
                  <md-select
                    name="product"
                    id="product"
                    v-model="tenant.product"
                  >
                    <md-option
                      v-for="product in products"
                      :key="product.id"
                      :value="product.id"
                    >
                      {{ product.description }}
                    </md-option>
                  </md-select>
                  <span class="md-error">{{ errors[0] }}</span>
                </md-field>
              </ValidationProvider>
            </div>

            <div class="md-layout">
              <div
                class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-50"
              >
                <ValidationProvider rules="required" v-slot="{ errors }">
                  <md-field :class="{ 'md-invalid': errors.length > 0 }">
                    <label>Country</label>
                    <md-select
                      name="country"
                      id="country"
                      v-model="tenant.country"
                    >
                      <md-option
                        v-for="country in countries"
                        :key="country.value.toLowerCase()"
                        :value="country.value.toLowerCase()"
                      >
                        {{ country.text }}
                      </md-option>
                    </md-select>
                    <span class="md-error">{{ errors[0] }}</span>
                  </md-field>
                </ValidationProvider>
              </div>

              <div
                class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-50"
              >
                <ValidationProvider rules="required" v-slot="{ errors }">
                  <md-field :class="{ 'md-invalid': errors.length > 0 }">
                    <label>Language</label>
                    <md-select
                      name="language"
                      id="language"
                      v-model="tenant.language"
                    >
                      <md-option
                        v-for="language in languages"
                        :key="language.value.toLowerCase()"
                        :value="language.value.toLowerCase()"
                      >
                        {{ language.text }}
                      </md-option>
                    </md-select>
                    <span class="md-error">{{ errors[0] }}</span>
                  </md-field>
                </ValidationProvider>
              </div>

              <div class="md-layout-item md-size-100">
                <ValidationProvider rules="required" v-slot="{ errors }">
                  <md-field :class="{ 'md-invalid': errors.length > 0 }">
                    <label>Deployment Environment</label>
                    <md-select
                      name="environment"
                      id="environment"
                      v-model="tenant.deploymentEnvironment"
                    >
                      <md-option
                        v-for="item in availableRegions"
                        :key="item.regionName"
                        :value="item.regionName"
                      >
                        {{ item.regionName }}
                      </md-option>
                    </md-select>
                    <span class="md-error">{{ errors[0] }}</span>
                  </md-field>
                </ValidationProvider>
              </div>

              <div class="md-layout-item md-size-100">
                <md-switch v-model="tenant.fscCloud">
                  Use MFSC cloud
                  <md-tooltip>
                    Customer
                    <b>
                      is ready to use
                      <br />
                      Cloud
                    </b>
                    version of
                    <br />
                    FRISS Screening Center
                  </md-tooltip>
                </md-switch>
                <md-switch v-model="tenant.detectionCloud">
                  Detection Cloud
                  <md-tooltip>
                    Customer
                    <b>
                      is ready to use
                      <br />
                      Cloud
                    </b>
                    version of
                    <br />
                    Detection
                  </md-tooltip>
                </md-switch>
                <md-switch v-model="tenant.newParser">
                  New expression parser
                  <md-tooltip>
                    Customer uses the <b>new</b><br/>
                    version of the KB<br/>
                    Rules Parser.
                  </md-tooltip>
                </md-switch>
              </div>
            </div>

            <div class="md-layout-item md-size-50">
              <div class="md-layout-item md-size-100">
                <h6><strong>First User Information</strong></h6>
                <ValidationProvider rules="required|email" v-slot="{ errors }">
                  <md-field :class="{ 'md-invalid': errors.length > 0 }">
                    <label>Email Address</label>
                    <md-input
                      v-model="tenant.firstUser.emailAddress"
                    ></md-input>
                    <span class="md-error">{{ errors[0] }}</span>
                  </md-field>
                </ValidationProvider>
              </div>
              <div class="md-layout">
                <div
                  class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-50"
                >
                  <ValidationProvider rules="required" v-slot="{ errors }">
                    <md-field :class="{ 'md-invalid': errors.length > 0 }">
                      <label>First Name</label>
                      <md-input v-model="tenant.firstUser.firstName"></md-input>
                      <span class="md-error">{{ errors[0] }}</span>
                    </md-field>
                  </ValidationProvider>
                </div>
                <div
                  class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-50"
                >
                  <ValidationProvider rules="required" v-slot="{ errors }">
                    <md-field :class="{ 'md-invalid': errors.length > 0 }">
                      <label>Last Name</label>
                      <md-input v-model="tenant.firstUser.lastName"></md-input>
                      <span class="md-error">{{ errors[0] }}</span>
                    </md-field>
                  </ValidationProvider>
                </div>
              </div>
              <div class="md-layout-item md-size-100">
                <div class="md-layout">
                  <div class="md-size-50">
                    <h6>
                      <strong>Additional Administrators</strong>
                    </h6>
                  </div>
                  <div class="md-layout-item md-size-10">
                    <md-button
                      class="md-just-icon md-simple md-primary"
                      @click="addAdmin()"
                      style="height: 15px"
                    >
                      <md-icon>add</md-icon>
                      <md-tooltip md-direction="top">
                        Add Another Administrator
                      </md-tooltip>
                    </md-button>
                  </div>
                </div>
              </div>

              <div
                class="md-layout"
                v-for="(admin, index) in tenant.additionalAdmins"
                :key="index"
              >
                <div
                  class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100"
                >
                  <ValidationProvider
                    rules="required|email"
                    v-slot="{ errors }"
                  >
                    <md-field
                      class="outer"
                      :class="{ 'md-invalid': errors.length > 0 }"
                    >
                      <label>Email Address</label>
                      <md-input v-model="admin.emailAddress"></md-input>
                      <md-button
                        class="md-just-icon md-simple md-danger md-tiny"
                        v-if="tenant.additionalAdmins.length > 0"
                        @click="removeAdmin(index)"
                      >
                        <md-icon>delete</md-icon>
                        <md-tooltip md-direction="top">Delete</md-tooltip>
                      </md-button>
                      <span class="md-error">{{ errors[0] }}</span>
                    </md-field>
                  </ValidationProvider>
                </div>
                <div
                  class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-50"
                >
                  <ValidationProvider rules="required" v-slot="{ errors }">
                    <md-field :class="{ 'md-invalid': errors.length > 0 }">
                      <label>First Name</label>
                      <md-input v-model="admin.firstName"></md-input>
                      <span class="md-error">{{ errors[0] }}</span>
                    </md-field>
                  </ValidationProvider>
                </div>
                <div
                  class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-50"
                >
                  <ValidationProvider rules="required" v-slot="{ errors }">
                    <md-field :class="{ 'md-invalid': errors.length > 0 }">
                      <label>Last Name</label>
                      <md-input v-model="admin.lastName"></md-input>
                      <span class="md-error">{{ errors[0] }}</span>
                    </md-field>
                  </ValidationProvider>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ValidationObserver>
    </md-dialog-content>

    <md-dialog-actions>
      <md-button @click="closeDialog">Close</md-button>
      <md-button class="md-primary" @click="save">Save</md-button>
    </md-dialog-actions>
  </md-dialog>
</template>

<script>
import { extend, ValidationProvider, ValidationObserver } from "vee-validate";
import { required, email, regex } from "vee-validate/dist/rules";

extend("required", { ...required, message: "This field is required" });

extend("email", { ...email, message: "This is not a valid email address" });

extend("regex", { ...regex, message: "Alpha characters, - and _ only." });

export default {
  name: "CreateEditTenant",
  components: {
    ValidationProvider,
    ValidationObserver
  },
  computed: {
    showDialog() {
      return this.$tenantStore.state.showCreateDialog;
    },
    languages() {
      return this.$tenantStore.state.languages;
    },
    countries() {
      return this.$tenantStore.state.countries;
    },
    isEditMode() {
      return !!this.tenant.id;
    },
    isFsc() {
      return (
        this.selectedDomains.includes("claims") ||
        this.selectedDomains.includes("underwriting") ||
        this.selectedDomains.includes("kyc")
      );
    }
  },
  async created() {
    this.selectedDomains = this.tenant.domain?.split(";").filter(Boolean) ?? [];

    try {
      const regionsResponse = await this.$featureFlagApi.get("/regions/names");
      this.availableRegions = regionsResponse.data;
    } catch (error) {
      this.$notify({
        message: "Failed to read data necessary to Deploy",
        icon: "running_with_errors",
        horizontalAlign: "right",
        verticalAlign: "bottom",
        type: "danger"
      });
    }
  },
  data() {
    return {
      tenant: {
        id: this.$tenantStore.state.tenantToEdit.id || null,
        tenantCode: this.$tenantStore.state.tenantToEdit.tenantCode || "",
        name: this.$tenantStore.state.tenantToEdit.name || "",
        product: this.$tenantStore.state.tenantToEdit.product || "",
        country: this.$tenantStore.state.tenantToEdit.country || "",
        language: this.$tenantStore.state.tenantToEdit.language || "",
        fscCloud: this.$tenantStore.state.tenantToEdit.fscCloud ?? false,
        deploymentEnvironment:
          this.$tenantStore.state.tenantToEdit.deploymentEnvironment || "",
        domain: this.$tenantStore.state.tenantToEdit.domain || "",
        tenantGroup:
          this.$tenantStore.state.tenantToEdit.tenantGroup ||
          this.$tenantStore.state.tenantToEdit.tenantCode,
        firstUser: this.$tenantStore.state.tenantToEdit.tenantAdmins
          ? this.$tenantStore.state.tenantToEdit.tenantAdmins[0]
          : {},
        additionalAdmins: this.$tenantStore.state.tenantToEdit.tenantAdmins
          ? this.$tenantStore.state.tenantToEdit.tenantAdmins.slice(
              1,
              this.$tenantStore.state.tenantToEdit.tenantAdmins.length
            )
          : [],
        provisions: this.$tenantStore.state.tenantToEdit.provisions,
        detectionCloud: this.$tenantStore.state.tenantToEdit.detectionCloud ?? true,
        newParser: this.$tenantStore.state.tenantToEdit.newParser ?? true,
      },
      selectedDomains: [],
      original: {
        tenantGroup: this.$tenantStore.state.tenantToEdit.tenantGroup || "",
        domain: this.$tenantStore.state.tenantToEdit.domain || ""
      },
      availableRegions: [],
      domains: [
        { id: "claims", description: "Claims" },
        { id: "underwriting", description: "Underwriting" },
        { id: "kyc", description: "KYC" },
        { id: "case", description: "Case" }
      ],
      products: [
        { id: "Claims", description: "Claims (FRISS)" },
        { id: "Underwriting", description: "Underwriting (FRISS)" },
        { id: "UnderwritingCLS", description: "Underwriting (CLS)" },
        { id: "UBO", description: "UBO" },
        { id: "UBOGlobal", description: "UBO Global" }
      ]
    };
  },
  methods: {
    closeDialog() {
      this.$tenantStore.closeCreateEditDialog();
    },
    getRegionHeader(key) {
      return key.replace(/([A-Z])/g, " $1").replace(/^./, function (str) {
        return str.toUpperCase();
      });
    },
    async save() {
      if (!this.isFsc) {
        this.tenant.product = "";
      }
      this.$refs.tenantForm.validate().then(async success => {
        if (!success) {
          return;
        }
        this.tenant.domain = this.selectedDomains.join(";");
        this.isEditMode ? await this.update() : await this.create();
        this.closeDialog();
      });
    },
    async create() {
      try {
        const newTenant = {
          id: null,
          tenantCode: this.tenant.tenantCode,
          name: this.tenant.name,
          product: this.tenant.product,
          country: this.tenant.country,
          language: this.tenant.language,
          fscCloud: this.tenant.fscCloud,
          deploymentEnvironment: this.tenant.deploymentEnvironment,
          domain: this.tenant.domain,
          tenantGroup: this.tenant.tenantGroup,
          tenantAdmins: [this.tenant.firstUser, ...this.tenant.additionalAdmins],
          detectionCloud: this.tenant.detectionCloud,
          newParser: this.tenant.newParser,
        };

        await this.$tenantStore.addTenant(newTenant);

        this.$notify({
          message: "Tenant created successfully!",
          icon: "add_alert",
          horizontalAlign: "right",
          verticalAlign: "bottom",
          type: "success"
        });

        await this.syncTenantGroupSettings(newTenant, null);
      } catch (error) {
        let message = "Failed to create new tenant.";
        if (error.response) {
          message += `: ${error.response.data.errors.join(" ")}`;
        }

        this.$notify({
          message: message,
          icon: "add_alert",
          horizontalAlign: "right",
          verticalAlign: "bottom",
          type: "danger"
        });
      }
    },
    async update() {
      try {
        const tenant = {
          id: this.tenant.id,
          tenantCode: this.tenant.tenantCode,
          name: this.tenant.name,
          product: this.tenant.product,
          country: this.tenant.country,
          language: this.tenant.language,
          fscCloud: this.tenant.fscCloud,
          deploymentEnvironment: this.tenant.deploymentEnvironment,
          domain: this.tenant.domain,
          tenantGroup: this.tenant.tenantGroup,
          tenantAdmins: [this.tenant.firstUser, ...this.tenant.additionalAdmins],
          detectionCloud: this.tenant.detectionCloud,
          newParser: this.tenant.newParser,
        };

        await this.$tenantStore.updateTenant(tenant);

        this.$notify({
          message: "Tenant updated successfully!",
          icon: "add_alert",
          horizontalAlign: "right",
          verticalAlign: "bottom",
          type: "success"
        });

        if (this.groupSettingsChanged(tenant)) {
          await this.syncTenantGroupSettings(tenant, this.original);
        }
      } catch (error) {
        let message = "Failed to update tenant.";
        if (error.response) {
          message += `: ${error.response.data.errors.join(" ")}`;
        }

        this.$notify({
          message: message,
          icon: "add_alert",
          horizontalAlign: "right",
          verticalAlign: "bottom",
          type: "danger"
        });
      }
    },
    groupSettingsChanged(tenant) {
      const normalizeDomains = domain =>
        (domain || "").toLowerCase().split(";").filter(Boolean).sort().join(";");

      return (
        (tenant.tenantGroup || "").toLowerCase() !== this.original.tenantGroup.toLowerCase() ||
        normalizeDomains(tenant.domain) !== normalizeDomains(this.original.domain)
      );
    },
    async syncTenantGroupSettings(tenant, previous) {
      if (!tenant.domain) {
        return;
      }

      try {
        const requestedBy = this.$msal.user.profile.email || this.$msal.user.userName;
        const result = await this.$tenantStore.syncTenantGroupSettings(tenant, previous, requestedBy);
        const environments = result?.environments || [];
        const problems = environments.filter(e => e.status === "Failed" || e.status === "PartiallySynced");
        const synced = environments.filter(e => e.status === "Synced" || e.status === "PartiallySynced");

        if (problems.length > 0) {
          const details = problems
            .map(e => `${e.environment}: ${[e.message, ...(e.conflicts || [])].filter(Boolean).join("; ")}`)
            .join(" | ");
          this.$notify({
            message: `Tenant group settings synced with issues. ${details}`,
            icon: "running_with_errors",
            horizontalAlign: "right",
            verticalAlign: "bottom",
            type: "warning",
            timeout: 15000
          });
          return;
        }

        this.$notify({
          message: synced.length > 0
            ? `Tenant group settings synced for ${synced.map(e => e.environment).join(", ")}.`
            : "Tenant group settings not synced: the tenant was not found in Identity in any environment.",
          icon: "add_alert",
          horizontalAlign: "right",
          verticalAlign: "bottom",
          type: synced.length > 0 ? "success" : "info"
        });
      } catch (error) {
        this.$notify({
          message: "Tenant saved, but syncing the tenant group settings failed.",
          icon: "running_with_errors",
          horizontalAlign: "right",
          verticalAlign: "bottom",
          type: "danger"
        });
      }
    },
    addAdmin() {
      this.tenant.additionalAdmins.push({
        emailAddress: "",
        firstName: "",
        lastName: ""
      });
    },
    removeAdmin(index) {
      this.tenant.additionalAdmins.splice(index, 1);
    }
  }
};
</script>

<style scoped>
.md-helper-text {
  padding-right: 25px;
  font-size: 11px !important;
}
</style>
